import { localizedStrings } from '@core/strings'
import React from 'react'
import { SignatureEdit } from 'webapp/components/common/Signature'
import {
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { useModal } from '@common/GlobalModal'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { Checklist } from '@core/entities/checklist/Checklist'
import {
  useApproveChecklist,
  useNominateChecklistApprovers,
} from '@core/react-query/features/checklists/checklist'
import { ITemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers'
import { ModalDialogContainer } from '@common/modal'
import NominateApproverModal from '@common/NominateApproverModal'
import { useToast } from '@core/toast'
import { ErrorMessage } from 'components/Messages'

interface ComponentType {
  checklist: Checklist
  approvalAnswers?: ITemplateFormAnswers
  onApprove?: () => void
}

export const ApproveChecklistModal: React.FC<ComponentType> = ({
  checklist,
  approvalAnswers,
  onApprove,
}) => {
  const { hideModal, showModal } = useModal()
  const { mutate, isLoading, error } = useApproveChecklist()
  const nominateChecklistApprovers = useNominateChecklistApprovers()
  const toast = useToast()
  const { data: profile } = useGetProfile()

  const approveChecklist = () => {
    const answers = approvalAnswers?.answers?.map((questionGroup) => {
      const questionGroupData = questionGroup?.data?.map((question) => {
        const submissionProofs = [
          ...(question?.submissionProofs || []),
          // @ts-ignore
          ...(question?.submissionProofFiles || []),
        ]

        const submissionDocuments = [
          ...(question?.submissionDocuments || []),
          // @ts-ignore
          ...(question?.submissionProofDocuments || []),
        ]

        return {
          ...question,
          submissionProofs,
          submissionDocuments,
        }
      })
      return { ...questionGroup, data: questionGroupData }
    })

    mutate(
      {
        checklistId: checklist.id,
        approvalId: checklist.approvalReviews?.nextApprovalId,
        templateId:
          checklist.template.id ??
          checklist.template.currentVersion?.templateId,
        approvalAnswers: { answers },
      },
      {
        onSuccess: (res) => {
          hideModal()
          toast.success(localizedStrings.checklistApproved)

          const updatedChecklist = Checklist.new(res)

          if (updatedChecklist.shouldShowNominateApprovers()) {
            showModal(
              <NominateApproverModal
                approvalLabel={
                  updatedChecklist.getNextApproval()?.approval?.label
                }
                description={localizedStrings.notifyApproversTextChecklist}
                suggestedApprovers={
                  updatedChecklist.approvalReviews?.nominatedApprovers
                    ?.suggestedApprovers ?? []
                }
                targetApprovers={
                  updatedChecklist.approvalReviews?.nominatedApprovers
                    ?.approvers?.[0]?.targetApprovers ?? []
                }
                onDismiss={() => {
                  onApprove && onApprove()
                  hideModal()
                }}
                onNominate={async (approvers) => {
                  await nominateChecklistApprovers.mutateAsync({
                    checklistId: updatedChecklist.id,
                    approvalId:
                      updatedChecklist.approvalReviews?.nextApprovalId,
                    approvers,
                  })
                  onApprove && onApprove()
                  hideModal()
                }}
              />,
            )
          } else {
            onApprove && onApprove()
          }
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.approve}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <SignatureEdit readonly value={profile?.signatureUrl} />
        {error && <ErrorMessage>{error.message}</ErrorMessage>}
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <Button
          variant='contained'
          onClick={approveChecklist}
          disabled={isLoading}
          data-test='jsConfirmApproveChecklist'
        >
          {localizedStrings.approve}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
